export const escapeRegex = (str: string) =>
  str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const INNER_TOKEN = '[~!@#$%^&*\\(\\)_+\\|`\\-={}\\\\[\\]<>?,./;\':"0-9\\s]*';

const removeBlankWords = (words: string[]) =>
  words.filter(word => word.trim().length > 0);

const sortByLengthDesc = (words: string[]) =>
  [...words].sort((a, b) => b.length - a.length);

const prepareWords = (words: string[]) =>
  sortByLengthDesc(removeBlankWords(words));

const createSingleRegex = (
  patterns: string[],
  flags = 'giu',
): RegExp | null => {
  if (patterns.length === 0) return null;
  return new RegExp(patterns.join('|'), flags);
};

export const createBlackListRegex = (words: string[]): RegExp | null => {
  const patterns = prepareWords(words).map(word => `(?:${escapeRegex(word)})`);
  return createSingleRegex(patterns);
};

export const createInnerRegex = (words: string[]): RegExp | null => {
  const patterns = prepareWords(words).map(word => {
    const chars = Array.from(word).map(char => escapeRegex(char));
    return `(?:${chars.join(INNER_TOKEN)})`;
  });
  return createSingleRegex(patterns);
};

export const createIsolatedRegex = (words: string[]): RegExp | null => {
  const escapedWords = prepareWords(words).map(word => escapeRegex(word));
  if (escapedWords.length === 0) return null;
  const alternation = escapedWords.join('|');
  // iOS Safari < 16.4는 lookbehind 미지원 → render 중 RegExp 생성에서 SyntaxError가 throw되어
  // 글로벌 ErrorBoundary로 페이지가 다운된다 (Sentry PLAY-APP-AHE / AHC / QUIZ-APP-73D).
  // 동등한 의미를 캡처 그룹 + lookahead로 표현. 그룹 1은 선행 공백/문자열 시작이며
  // 치환 시 보존이 필요하다 — replace 호출부에서 `$1` 또는 매칭 그룹을 유지해야 한다.
  return new RegExp(`(^|\\s)(?:${alternation})(?=\\s|$)`, 'giu');
};

export const createWhiteListToken = (index: number) => `\uE000${index}\uE001`;

// 토큰 경계 문자(유니코드 PUA). 입력에 남으면 토큰 위조가 가능하므로 먼저 제거한다.
const WHITE_LIST_SENTINEL_REGEX = /[\uE000\uE001]/g;

export interface BanWordsData {
  blackList: string[];
  whiteList: string[];
  isolated: string[];
  inner: string[];
}

export interface BanWordsRegexPatterns {
  whiteListRegex: RegExp | null;
  blackListRegex: RegExp | null;
  innerRegex: RegExp | null;
  isolatedRegex: RegExp | null;
}

export const createRegexPatterns = (
  data: BanWordsData,
  additionalBlackList?: string[],
): BanWordsRegexPatterns => {
  const allBlackList = additionalBlackList
    ? [...data.blackList, ...additionalBlackList]
    : data.blackList;

  return {
    whiteListRegex: createBlackListRegex(data.whiteList),
    blackListRegex: createBlackListRegex(allBlackList),
    innerRegex: createInnerRegex(data.inner),
    isolatedRegex: createIsolatedRegex(data.isolated),
  };
};

export const processWithWhiteList = <T>(
  message: string,
  whiteListRegex: RegExp | null,
  processor: (processedMessage: string) => T,
): T => {
  // 토큰 위조 방지 — 안 지우면 복원 단계 replace 가 사용자가 심은 토큰까지 치환해
  // 임의의 화이트리스트 단어를 주입할 수 있다.
  const sanitizedMessage = message.replace(WHITE_LIST_SENTINEL_REGEX, '');

  if (!whiteListRegex) return processor(sanitizedMessage);

  let processedMessage = sanitizedMessage;
  const whiteListMap = new Map<string, string>();
  let tokenIndex = 0;

  whiteListRegex.lastIndex = 0;
  processedMessage = processedMessage.replace(whiteListRegex, match => {
    const token = createWhiteListToken(tokenIndex);
    whiteListMap.set(token, match);
    tokenIndex++;
    return token;
  });

  let result = processor(processedMessage);

  if (typeof result === 'string') {
    whiteListMap.forEach((original, token) => {
      result = (result as string).replace(
        new RegExp(escapeRegex(token), 'g'),
        original,
      ) as T;
    });
  }

  return result;
};
